import { analytics } from 'Modules/Analytics'
import { ChecklistItemWithResourceInfo, User } from 'TypeDefs/Goal'
import { goals } from 'Modules/Analytics/trackActionInterface/Impact/Goals'
import { initiatives } from 'Modules/Analytics/trackActionInterface/Impact/Initiatives'
import { GoalType } from 'Generated/graphql-types'
import { TabFilterKeyEnum } from '../../ImpactReportDashboard/types'

type BaseEventProps = {
  assigneeUserEmail?: string | null
  assignerEmail?: string
  assignerId: number
  dueDate?: string | null
  funderOrgId: number | null
  goalId: number | undefined
  goalType: GoalType
  isGoalSharedWithFunder: boolean
  nonprofitOrgId: number
  ownerId?: number | null
  platformPlan: string | null
  taskId?: number | undefined
  taskUpdatedDate?: string
  templateLibraryId: number | null
  userId: number
  userRole: string
}

type RecurrenceOptions = {
  dayOfWeek: number
  dayOfMonth: number
  endDate: string
  frequency: number
  recurrenceType: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY'
  startDate: string
  weekOfMonth: number
}

export const taskAnalytics = {
  dispatchTaskCompleted: (
    task: ChecklistItemWithResourceInfo,
    eventProps: BaseEventProps & { assigneeUserEmail: string | null }
  ) => {
    analytics.trackActionCreator(goals.Goal_Task_Completed)({
      ...eventProps,
      assigneeUserEmail: eventProps.assigneeUserEmail,
      dueDate: task.dueDate,
      resourceId: task.resource?.id || null,
      taskDescription: task.title,
      taskId: task.id,
      taskStatus: true,
      taskUpdatedDate: task.updatedAt,
    })
  },

  dispatchTaskDueDateSubmitted: (
    task: ChecklistItemWithResourceInfo,
    newDueDate: string | null,
    eventProps: BaseEventProps
  ) => {
    analytics.trackActionCreator(initiatives.Goal_Task_DueDate_Submitted)({
      ...eventProps,
      dueDate: newDueDate,
      taskDescription: task.title,
      taskId: task.id,
      taskStatus: task.isCompleted ? 'complete' : 'pending',
      taskUpdatedDate: task.updatedAt,
      resourceId: task.resource?.id,
      linkText: task.linkText,
      linkUrl: task.linkUrl,
    })
  },

  dispatchTaskDueDateDeleted: (
    task: ChecklistItemWithResourceInfo,
    eventProps: BaseEventProps
  ) => {
    analytics.trackActionCreator(initiatives.Goal_Task_DueDate_Delete_Clicked)({
      ...eventProps,
      dueDate: null,
      taskDescription: task.title,
      taskId: task.id,
      taskStatus: task.isCompleted ? 'complete' : 'pending',
      taskUpdatedDate: task.updatedAt,
      resourceId: task.resource?.id,
      linkText: task.linkText,
      linkUrl: task.linkUrl,
    })
  },

  dispatchTaskAssigned: (
    task: ChecklistItemWithResourceInfo,
    newOwner: User | null | undefined,
    eventProps: BaseEventProps & { assigneeUserEmail: string | null }
  ) => {
    analytics.trackActionCreator(goals.Goal_Task_Assigned)({
      ...eventProps,
      assigneeUserId: newOwner?.id || null,
      assigneeUserEmail: eventProps.assigneeUserEmail,
      dueDate: task.dueDate,
      goalUpdatedDate: task.updatedAt,
      goalTaskAssignationStatus: newOwner ? 'complete' : 'pending',
      taskDescription: task.title,
      taskId: task.id,
      taskStatus: task.isCompleted ? 'complete' : 'pending',
      taskUpdatedDate: task.updatedAt,
    })
  },

  dispatchTaskAssignedNotificationSent: (
    task: ChecklistItemWithResourceInfo,
    eventProps: BaseEventProps
  ) => {
    analytics.trackActionCreator(
      initiatives.Goal_Task_Assigned_Notification_Sent
    )({
      ...eventProps,
      taskId: task.id,
    })
  },

  dispatchAcademyResourceClicked: (
    task: ChecklistItemWithResourceInfo,
    eventProps: BaseEventProps
  ) => {
    if (task.resource) {
      analytics.trackActionCreator(initiatives.Goal_Task_Academy_Link_Clicked)({
        ...eventProps,
        taskId: task.id,
        resourceId: task.resource.id,
      })
    }
  },

  dispatchGoalTaskCreated: (
    eventProps: BaseEventProps &
      Partial<RecurrenceOptions> & {
        taskDescription: string
      }
  ) => {
    analytics.trackActionCreator(initiatives.Goal_Task_Created)({
      ...eventProps,
    })
  },

  dispatchGoalTaskEdited: (
    eventProps: BaseEventProps &
      Partial<RecurrenceOptions> & {
        taskDescription: string
      }
  ) => {
    analytics.trackActionCreator(initiatives.Goal_Task_Edited)({
      ...eventProps,
    })
  },

  dispatchGoalTaskSaveClicked: (
    task: ChecklistItemWithResourceInfo,
    eventProps: BaseEventProps
  ) => {
    analytics.trackActionCreator(initiatives.Goal_Task_Save_Clicked)({
      ...eventProps,
      dueDate: task.dueDate,
      resourceId: task.resource?.id,
      taskDescription: task.title,
      taskStatus: task.isCompleted ? 'complete' : 'pending',
      taskUpdatedDate: task.updatedAt,
      linkText: task.linkText,
      linkUrl: task.linkUrl,
    })
  },

  dispatchGoalTaskCancelClicked: (
    task: ChecklistItemWithResourceInfo,
    eventProps: BaseEventProps & { taskUpdatedDate: string }
  ) => {
    analytics.trackActionCreator(initiatives.Goal_Task_Cancel_Clicked)({
      ...eventProps,
      dueDate: task.dueDate,
      resourceId: task.resource?.id,
      taskDescription: task.title,
      taskStatus: task.isCompleted ? 'complete' : 'pending',
      taskUpdatedDate: task.updatedAt,
      linkText: task.linkText,
      linkUrl: task.linkUrl,
    })
  },

  dispatchGoalAddTaskClicked: (eventProps: BaseEventProps) => {
    analytics.trackActionCreator(initiatives.Goal_Add_Task_Clicked)(eventProps)
  },

  dispatchEditRecurringTaskClicked: (eventProps: BaseEventProps) => {
    analytics.trackActionCreator(goals.Edit_Recurring_Task_Clicked)(eventProps)
  },

  dispatchDuplicateRecurringTaskClicked: (eventProps: BaseEventProps) => {
    analytics.trackActionCreator(goals.Duplicate_Recurring_Task_Clicked)(
      eventProps
    )
  },

  dispatchDeleteRecurringTaskClicked: (eventProps: BaseEventProps) => {
    analytics.trackActionCreator(goals.Delete_Recurring_Task_Clicked)(
      eventProps
    )
  },

  dispatchDeleteRecurringTaskOptionSaved: (
    eventProps: BaseEventProps & { deleteOption: string }
  ) => {
    analytics.trackActionCreator(goals.Delete_Recurring_Task_Option_Saved)(
      eventProps
    )
  },

  dispatchDeleteRecurringTaskDeleted: (eventProps: { taskId: number }) => {
    analytics.trackActionCreator(goals.Delete_Task)(eventProps)
  },

  dispatchGoalTaskEditTaskClicked: (eventProps: BaseEventProps) => {
    analytics.trackActionCreator(initiatives.Goal_Task_EditTask_Clicked)(
      eventProps
    )
  },

  dispatchGoalTaskDuplicateTaskClicked: (eventProps: BaseEventProps) => {
    analytics.trackActionCreator(initiatives.Goal_Task_DuplicateTask_Clicked)(
      eventProps
    )
  },

  dispatchGoalTaskDeleteTaskClicked: (eventProps: BaseEventProps) => {
    analytics.trackActionCreator(initiatives.Goal_Task_DeleteTask_Clicked)(
      eventProps
    )
  },

  dispatchTabClicked: (
    eventProps: BaseEventProps,
    tabFilterKey: TabFilterKeyEnum
  ) => {
    const tabFilterKeyToEventMap = {
      all: goals.All_Clicked,
      assignedToMe: goals.Assigned_Me_Clicked,
      completed: goals.Completed_Clicked,
      pastDue: goals.Past_Due_Clicked,
      todo: goals.To_Do_Clicked,
      unassigned: goals.Unassigned_Clicked,
    }

    const eventToFire = tabFilterKeyToEventMap[tabFilterKey]
    if (eventToFire) analytics.trackActionCreator(eventToFire)(eventProps)
    else console.warn(`No event found for tab: ${tabFilterKey}`)
  },
}
